import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ProctoringEnableConfigDialogBoxComponent } from '../proctoring-enable-config-dialog-box/proctoring-enable-config-dialog-box.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, Subscription, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GetProctoringConfiguration, GetProctoringConfigurationsPagedRequest } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctroing-config';
import { ProctoringDeleteConfigComponent } from '../proctoring-delete-config/proctoring-delete-config.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { MatMenuModule } from '@angular/material/menu';

@Component({
	selector: 'app-proctoring-rules-config',
	standalone: true,
	imports: [CommonModule, FormsModule, RouterModule, MatDialogModule,
		MatProgressSpinnerModule, MatTooltipModule, HasPermissionDirective, MatMenuModule
	],
	templateUrl: './proctoring-rules-config.component.html',
	styleUrl: './proctoring-rules-config.component.scss'
})
export class ProctoringRulesConfigComponent implements OnInit, OnDestroy {

	searchText: string = '';
	showColumnDropdown = false;
	pageSize = 10;
	currentPage = 1;
	totalPages = 0
	fromDate: string = '';
	toDate: string = '';
	showEnablePopup = false;
	openMenuIndex: number | null = null;
	isLoading = false;
	isInitialLoad = true;
	subscription = new Subscription();
	configData: GetProctoringConfiguration[] = [];
	total = 0;
	sortField = '';
	sortOrder: 'asc' | 'desc' = 'desc';
	searchSubject = new Subject<string>();
	companyId = '';
	PermissionForStudents = PermissionForStudents;


	constructor(
		private router: Router,
		private dialog: MatDialog,
		private proctoringService: ProctoringService,
		private toastr: ToastrService
	) { }


	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';
		this.subscription.add(
			this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
				this.currentPage = 1;
				this.getProctoringConfiguration();
			}));
		this.getProctoringConfiguration();
	}


	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		if (!target.closest('.column-filter')) {
			this.showColumnDropdown = false;
		}
		if (!target.closest('.action-menu')) {
			this.openMenuIndex = null;
		}
	}


	openEnablePopup(event: Event) {
		event.stopPropagation();
		this.openMenuIndex = null;
		this.dialog.open(ProctoringEnableConfigDialogBoxComponent);
	}


	goToConfigDetailPage(configId: string) {
		this.router.navigate(['/proctoring-dashboard/configuration-details', configId]);
	}


	toggleMenu(index: number, event: Event) {
		event.stopPropagation();

		this.openMenuIndex =
			this.openMenuIndex === index ? null : index;
	}


	getProctoringConfiguration() {
		this.isLoading = true;

		const payload: GetProctoringConfigurationsPagedRequest = {
			page: this.currentPage,
			pageSize: this.pageSize,
			filters: {
				companyId: {
					equals: this.companyId
				}
			},
			sort: [
				{
					field: this.sortField || 'createdAt',
					order: (this.sortOrder ? this.sortOrder.toUpperCase() : 'DESC') as 'ASC' | 'DESC'
				}
			],
			all: false
		};

		if (this.searchText && this.searchText.trim() !== '') {
			payload.search = this.searchText;
		}
		this.subscription.add(
			this.proctoringService.getProctoringConfiguration(payload).pipe(
				tap(response => {
					if (response.success) {
						this.configData = response.data.data;
						this.total = response.data.pagination.total;
						this.currentPage = response.data.pagination.page;
						this.pageSize = response.data.pagination.pageSize
						this.totalPages = response.data.pagination.totalPages;
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
					this.isInitialLoad = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		)
	}


	onSearch() {
		this.currentPage = 1;
		this.getProctoringConfiguration();
	}


	previousPage() {
		if (this.currentPage > 1) {
			this.currentPage--;
			this.getProctoringConfiguration();
		}
	}


	nextPage() {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.getProctoringConfiguration();
		}
	}


	goToPage(page: number) {
		if (page >= 1 && page <= this.totalPages) {
			this.currentPage = page;
			this.getProctoringConfiguration();
		}
	}


	get pagesArray(): number[] {
		const pages = this.totalPages || Math.ceil(this.total / this.pageSize) || 1;
		const arr = Array.from({ length: pages }, (_, i) => i + 1);
		return arr;
	}


	get displayTotalPages(): number {
		return this.totalPages || Math.ceil(this.total / (this.pageSize || 10)) || 1;
	}


	changePageSize() {
		this.currentPage = 1;
		this.getProctoringConfiguration();
	}


	get startRecord(): number {
		return this.total === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
	}


	get endRecord(): number {
		return Math.min(this.currentPage * this.pageSize, this.total);
	}


	sortBy(field: string) {
		if (this.sortField === field) {
			this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortField = field;
			this.sortOrder = 'asc';
		}
		this.currentPage = 1;
		this.getProctoringConfiguration();
	}

	onSearchChange() {
		this.searchSubject.next(this.searchText);
	}


	deleteConfigDialogBox(configId: string) {
		this.subscription.add(
			this.proctoringService.deleteProctoringConfig(configId).pipe(
				tap(response => {
					if (response.success) {
						this.toastr.success(response.message);
						this.sortField = '';
						this.sortOrder = 'desc';
						this.getProctoringConfiguration();
					} else {
						this.toastr.error(response.message);
					}
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	openDeleteDialogBox(configId: string, name: string) {
		this.openMenuIndex = null;
		const dialogRef = this.dialog.open(ProctoringDeleteConfigComponent, {
			data: {
				name,
				type: 'configuration'
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.deleteConfigDialogBox(configId);
		});
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}